import React, { lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCustomDomain } from '../contexts/CustomDomainContext';
import Spinner from '../components/Spinner';

// ── Lazy pages ────────────────────────────────────────────────────────────────

const AppLayout = lazy(() => import('../components/layout/AppLayout'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage'));
const MergeAccountsPage = lazy(() => import('../pages/MergeAccountsPage'));
const MfaChallengePage = lazy(() => import('../pages/MfaChallengePage'));
const EarlyAccessExpiredPage = lazy(() => import('../pages/EarlyAccessExpiredPage'));
const LinkExpiredPage = lazy(() => import('../pages/LinkExpiredPage'));
const LandingPage = lazy(() => import('../pages/public/landing'));
const ProPage = lazy(() => import('../pages/public/pro'));
const EbookExportPage = lazy(() => import('../pages/ebook/export'));
const JournalsHome = lazy(() => import('../pages/journals/JournalsHome'));
const JournalReader = lazy(() => import('../pages/journals/JournalReader'));
const JournalEditor = lazy(() => import('../pages/journals/JournalEditor'));
const MyJournals = lazy(() => import('../pages/journals/MyJournals'));
const JournalDrafts = lazy(() => import('../pages/journals/JournalDrafts'));
const JournalBookmarks = lazy(() => import('../pages/journals/JournalBookmarks'));
const JournalsByTag = lazy(() => import('../pages/journals/JournalsByTag'));
const JournalSeriesPage = lazy(() => import('../pages/journals/JournalSeriesPage'));
const CreatorDashboard = lazy(() => import('../pages/creator/dashboard'));
const ProfilePage = lazy(() => import('../pages/profile'));
const SearchPage = lazy(() => import('../pages/SearchPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const PaymentSuccessPage = lazy(() => import('../pages/PaymentSuccessPage'));
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const MyReports = lazy(() => import('../pages/journals/MyReports'));
const ContentCalendar = lazy(() => import('../pages/journals/ContentCalendar'));
const MyCollaborations = lazy(() => import('../pages/journals/MyCollaborations'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const ReferralPage = lazy(() => import('../pages/public/referrals'));
const GiftRedeemPage = lazy(() => import('../pages/GiftRedeemPage'));
const ProGiftRedeemPage = lazy(() => import('../pages/ProGiftRedeemPage'));
const TokensPage = lazy(() => import('../pages/TokensPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const AiTransparencyPage = lazy(() => import('../pages/AiTransparencyPage'));
const LegalPage = lazy(() => import('../pages/LegalPage'));
const OnboardingPage = lazy(() => import('../pages/onboarding'));

// ── Guards ────────────────────────────────────────────────────────────────────

/**
 * Paths that an authenticated-but-not-yet-onboarded user is allowed to visit
 * without being bounced back to /onboarding. /onboarding itself obviously
 * needs to be in here, plus any flow the user might land on out of band:
 *   - /verify-email   — email-verification deep link (token in query string)
 *   - /payment/success — Stripe redirect target for tipping/gifting flows
 *   - /legal/*        — terms/privacy/etc. links from the registration card
 *   - /logout / login — escape hatches if the user wants to back out
 *
 * Everything else funnels through /onboarding until the flag flips. Settings
 * is intentionally NOT in this list — a user who upgrades via Stripe lands
 * back at /settings?pro=success#billing, the guard kicks in, and they get
 * redirected to /onboarding where the auto-advance hook reads their new
 * tier and skips them straight to the profile step.
 */
const ONBOARDING_BYPASS_PREFIXES = [
  '/onboarding',
  '/verify-email',
  '/payment/success',
  '/legal',
  '/login',
  '/logout',
  '/early-access-expired',
  '/link-expired',
  '/mfa-challenge',
  '/merge-accounts',
];

function isOnboardingBypassed(pathname: string): boolean {
  return ONBOARDING_BYPASS_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

/**
 * Redirects unauthenticated users to /login. If the user IS authenticated
 * but hasn't finished the welcome flow, redirects to /onboarding instead
 * (unless the current route is itself part of the onboarding bypass list).
 */
export function RequireAuth({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && !user.hasCompletedOnboarding && !isOnboardingBypassed(location.pathname)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/**
 * Landing page for guests, journal feed for authenticated users.
 *
 * While auth is still loading, we render <LandingPage /> rather than a spinner
 * for two reasons:
 *
 *   1. Prerender hydration. The root route (/) is prerendered as LandingPage
 *      at build time by scripts/prerender.mjs. On first client render in a
 *      real browser, AuthProvider starts with isLoading=true. If we returned
 *      <Spinner /> here, React would hit a hydration mismatch (prerendered DOM
 *      = LandingPage, client render = Spinner) and throw the prerendered
 *      content away — defeating the whole point of prerendering.
 *
 *   2. Faster first paint for unauthenticated users. The majority of cold
 *      loads to / are anonymous visitors. Showing them LandingPage immediately
 *      beats a spinner-then-landing flicker.
 *
 * Tradeoff: authenticated users who navigate to / directly (unusual — most
 * land on /dashboard or a feed-shaped URL) will briefly see LandingPage
 * before auth resolves and they're rerendered as JournalsHome. This is a
 * minor regression versus the spinner-first behaviour and acceptable given
 * the SEO payoff.
 */
export function RootRoute(): JSX.Element {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { handle, isCustomDomain, isLoading: domainLoading } = useCustomDomain();

  // Still resolving the custom domain hostname — hold rendering briefly
  // to avoid a flash of the wrong page (landing/feed) before redirecting.
  // domainLoading is only true on non-native hostnames, so this branch is
  // not hit during prerender (127.0.0.1 is native) and thus doesn't affect
  // hydration on the main domain.
  if (domainLoading) return <Spinner />;

  // Custom domain root: redirect to /:handle so the normal profile route takes over.
  // e.g. writings.alice.com/ → /alice → <ProfilePage username="alice" />
  if (isCustomDomain && handle) {
    return <Navigate to={`/${handle}`} replace />;
  }

  // While auth is loading, render the landing page — matches prerender output
  // and avoids a spinner flicker for anonymous visitors. See JSDoc above.
  if (authLoading) return <LandingPage />;

  // Authenticated but hasn't finished the welcome flow → /onboarding.
  // Anonymous → landing. Onboarded → feed.
  if (isAuthenticated && user && !user.hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  return isAuthenticated ? <JournalsHome /> : <LandingPage />;
}

// ── Route tree ────────────────────────────────────────────────────────────────

export default function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* ── Public auth ───────────────────────────────────────────────────── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/merge-accounts" element={<MergeAccountsPage />} />

      {/* Onboarding lives outside the AppLayout shell — full-bleed welcome
          surface that should not show the navbar or app footer. Auth is
          required (no guest can land here in a meaningful state) and the
          page itself redirects authenticated-but-already-onboarded users
          back to /. Declared before /:username for the same reason as
          /mfa-challenge below. */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />
      {/* Landed on after an OAuth callback when the account has MFA enabled.
          See backend/src/routes/auth.ts — the OAuth handler redirects here
          with ?token=<challengeToken>&via=oauth instead of completing the
          session inline. Must be declared before /:username or the profile
          catch-all swallows it. */}
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />

      {/* ── Early-access expiry pages ─────────────────────────────────────── */}
      {/* Navigated to programmatically by EarlyAccessProvider when a grant
          becomes invalid or an invite link fails to redeem. Public routes —
          no guards — because the user is by definition not authenticated
          when they see these. */}
      <Route path="/early-access-expired" element={<EarlyAccessExpiredPage />} />
      <Route path="/link-expired" element={<LinkExpiredPage />} />

      {/* ── App shell (persistent nav + layout) ──────────────────────────── */}
      <Route element={<AppLayout />}>
        <Route path="/" element={<RootRoute />} />
        <Route path="/pro" element={<ProPage />} />
        <Route path="/pro/ebook-export" element={<EbookExportPage />} />
        <Route path="/ai-transparency" element={<AiTransparencyPage />} />
        <Route path="/referrals" element={<ReferralPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/tag/:tag" element={<JournalsByTag />} />
        <Route path="/journals/tag/:tag" element={<JournalsByTag />} />
        <Route path="/series/:id" element={<JournalSeriesPage />} />

        {/* ── Authenticated routes ────────────────────────────────────────── */}
        <Route
          path="/write"
          element={
            <RequireAuth>
              <JournalEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/write/:id"
          element={
            <RequireAuth>
              <JournalEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/my-journals"
          element={
            <RequireAuth>
              <MyJournals />
            </RequireAuth>
          }
        />
        <Route
          path="/drafts"
          element={
            <RequireAuth>
              <JournalDrafts />
            </RequireAuth>
          }
        />
        <Route
          path="/bookmarks"
          element={
            <RequireAuth>
              <JournalBookmarks />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <CreatorDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/my-reports"
          element={
            <RequireAuth>
              <MyReports />
            </RequireAuth>
          }
        />
        <Route
          path="/collaborations"
          element={
            <RequireAuth>
              <MyCollaborations />
            </RequireAuth>
          }
        />
        <Route
          path="/content-calendar"
          element={
            <RequireAuth>
              <ContentCalendar />
            </RequireAuth>
          }
        />

        {/* ── Misc public ─────────────────────────────────────────────────── */}
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/gift/redeem" element={<GiftRedeemPage />} />
        <Route path="/pro/gift/redeem" element={<ProGiftRedeemPage />} />
        <Route path="/journals/:username/:slug" element={<JournalReader />} />

        {/* Tokens — declared before /:username to prevent catch-all collision */}
        <Route
          path="/tokens"
          element={
            <RequireAuth>
              <TokensPage />
            </RequireAuth>
          }
        />
        <Route
          path="/notifications"
          element={
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          }
        />
        {/* ── Legal / policy — must be declared before /:username ─────────── */}
        <Route path="/legal/:slug" element={<LegalPage />} />

        {/* /u/:username is the canonical "view profile" URL — safe against reserved-path conflicts */}
        <Route path="/u/:username" element={<ProfilePage />} />
        <Route path="/:username" element={<ProfilePage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
